import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Lock, Loader2, ArrowLeft, Mail } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { formatApiError } from "@/lib/api";

/**
 * Public /admin/forgot-password page.
 *
 * Always renders a generic success message on submit — same policy as the
 * server so we never leak whether an admin email exists. If the address is
 * real, a reset link arrives within a minute or two via Resend.
 */
export default function AdminForgotPassword() {
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [sent, setSent] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        try {
            setLoading(true);
            await api.post("/admin/forgot-password", { email: email.trim() });
        } catch (err) {
            // Server returns 200 in the happy path. If we're here something
            // unusual (rate-limit backing off, etc.) — still show the same
            // success state so we don't leak details.
            // eslint-disable-next-line no-console
            console.warn("[AdminForgotPassword] request failed:", formatApiError(err));
        } finally {
            setLoading(false);
            setSent(true);
        }
    };

    return (
        <section className="min-h-[80vh] flex items-center bg-brand-navy" data-testid="admin-forgot-page">
            <div className="container-page max-w-md">
                <div className="card-soft p-8">
                    <div className="w-12 h-12 rounded-xl bg-brand-navy text-white flex items-center justify-center">
                        {sent ? <Mail size={20} /> : <Lock size={20} />}
                    </div>
                    <h1 className="mt-5 font-heading font-bold text-2xl text-brand-navy dark:text-slate-100">
                        {sent ? "Check your inbox" : "Reset your admin password"}
                    </h1>
                    <p className="text-brand-muted text-sm mt-1">
                        {sent
                            ? "If an admin account exists for that email, a password reset link has been sent. The link expires in 30 minutes."
                            : "Enter your admin email — we'll send a one-click reset link."}
                    </p>

                    {!sent ? (
                        <form onSubmit={submit} className="mt-6 space-y-4">
                            <div>
                                <Label htmlFor="fp-email">Admin email</Label>
                                <Input
                                    id="fp-email"
                                    type="email"
                                    autoFocus
                                    required
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    data-testid="admin-forgot-email"
                                    className="mt-2"
                                    placeholder="admin@simonara.com"
                                />
                            </div>
                            <button
                                type="submit"
                                disabled={loading || !email.trim()}
                                data-testid="admin-forgot-submit"
                                className="btn-navy w-full disabled:opacity-60"
                            >
                                {loading
                                    ? <><Loader2 size={18} className="mr-2 animate-spin" /> Sending…</>
                                    : "Send reset link"}
                            </button>
                        </form>
                    ) : (
                        <div className="mt-6 rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-4 text-sm text-emerald-800 dark:text-emerald-300" data-testid="admin-forgot-success">
                            Didn&apos;t get it? Check spam. If the address doesn&apos;t belong to an admin
                            account we won&apos;t send anything — this is a security measure to prevent
                            account enumeration.
                        </div>
                    )}

                    <Link
                        to="/admin"
                        data-testid="admin-forgot-back"
                        className="mt-6 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground underline"
                    >
                        <ArrowLeft size={12} /> Back to admin sign-in
                    </Link>
                </div>
            </div>
        </section>
    );
}
